import { React, useState, useContext, useEffect, useRef } from "react";
import { Container, MTabs, Flex, Heading, Modal, Button, MNotification } from "@cisco/spaces-dash-lib-styleguide";
import { v4 as uuidv4 } from "uuid";
import { useNotification } from "@cisco/spaces-dash-app-business-hierarchy";
import useLoader from "../../hooks/useLoader";
import * as PartnerDashboardService from "../../service/partner-dashboard-service";
import { I18nContext } from "../../contexts/i18nContext";
import DefaultConfig from "./default-config.json";
import PartnerEventsList from "../../assets/data/partners-events-list.json";
import "./manage-app.scss";
import AppCenterTab from "./app-center-tab/app-center-tab";
import UseCaseTab from "./usecase-tab/usecase-tab";
import AppTileTab from "./app-tile-tab/app-tile-tab";
import BehavioursTab from "./behaviours-tab/behaviours-tab";
import EventsTab from "./events-tab/events-tab";
import IntegrationDetailsTab from "./integration-details-tab/integration-details-tab";
import CustomTncTab from "./custom-tnc-tab/custom-tnc-tab";
import CloudIcon from "../../assets/images/partner/cloud-tag.svg";
import OnpremIcon from "../../assets/images/partner/onprem-tag.svg";
import AWSRegionJson from "./aws-regions.json";
import { PropertiesContext } from "../../contexts/propertiesContext";

const ManageApp = (props) => {
  const { properties } = useContext(PropertiesContext);
  const { action, type, hideManageModal } = props;
  const i18n = useContext(I18nContext);
  const { setToastMessage } = useNotification();
  const [app, setApp] = useState([]);
  const [partnerEvents, setPartnerEvents] = useState([]);
  const [industryFilter, setIndustryFilter] = useState([]);
  const [useCaseCategories, setUseCaseCategories] = useState([]);
  const [technologyFilter, setTechnologyFilter] = useState([]);
  const [appConfig, setLiveConfig] = useState([]);
  const [isManageAppModalOpen, setIsManageAppModalOpen] = useState(true);
  const [selectedTab, setSelectedTab] = useState("app-center");
  const [euRegionAvailable, setEuRegionAvailable] = useState(false);
  const [sgRegionAvailable, setSgRegionAvailable] = useState(false);
  const [confirmBrowserBackModal, setConfirmBrowserBackModal] = useState(false);
  const isInitialMount = useRef(true); // Ref to track initial mount
  const [appEndpoints, setAppEndpoints] = useState([
    {
      type: "KINESIS_FIREHOSE",
      config: [
        { key: "awsRegion", value: "" },
        { key: "deliveryStreamName", value: "" },
        { key: "awsAccessKey", value: "" },
        { key: "awsSecretKey", value: "" }
      ]
    },
    {
      type: "KINESIS",
      config: [
        { key: "awsRegion", value: "" },
        { key: "streamName", value: "" },
        { key: "awsAccessKey", value: "" },
        { key: "awsSecretKey", value: "" }
      ]
    },
    {
      type: "EVENT_HUBS",
      config: [{ key: "connectionString", value: "" }]
    },
    {
      type: "PUB_SUB",
      config: [
        { key: "projectId", value: "" },
        { key: "topicId", value: "" }
      ]
    }
  ]);
  const { setLoading } = useLoader();
  const [allPartnerEvents, setAllPartnerEvents] = useState(PartnerEventsList.events);

  const prepareAppEndpoints = () => {
    if (app && app.endpoints && app.endpoints.length) {
      const newAppEndpoints = [...appEndpoints];
      newAppEndpoints.forEach((endpoint, i) => {
        const appEndpoint = app.endpoints.find((ep) => ep.type === endpoint.type);
        if (appEndpoint) {
          newAppEndpoints[i].config = appEndpoint.config;
        }
      });
      setAppEndpoints(newAppEndpoints);
    }
  };

  useEffect(() => {
    const handlePopState = (event) => {
      // if (confirmBrowserBackModal) {
      event.preventDefault();
      // Only show modal if it's NOT the initial mount (i.e., back button was pressed)
      if (!isInitialMount.current) {
        isInitialMount.current = true;
        setConfirmBrowserBackModal(true);
        window.history.pushState(null, document.title, window.location.href); // Prevent back navigation
      } else {
        isInitialMount.current = false; // Set to false after the first render
      }
      // }
    };

    window.addEventListener("popstate", handlePopState);
    window.history.pushState({ modalOpened: false }, "");

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (app && app.endpoints && app.endpoints.length) {
      prepareAppEndpoints();
    }
  }, [app]);

  const closeManageModal = () => {
    setIsManageAppModalOpen(false);
    hideManageModal();
    setApp({});
  };

  const closeBrowserBackModal = () => {
    setConfirmBrowserBackModal(false);
  };

  const leaveBrowserModalPage = () => {
    closeManageModal();
  };

  const getPartnerRegionStatus = () => {
    PartnerDashboardService.enablePartnerStatus("eu", true)
      .then((response) => {
        if (response && response.data) {
          if (response.data.status) {
            setEuRegionAvailable(true);
          } else {
            setEuRegionAvailable(false);
          }
        } else {
          setEuRegionAvailable(false);
        }
      })
      .catch((error) => {
        setEuRegionAvailable(false);
        console.log(error);
      });

    PartnerDashboardService.enablePartnerStatus("sg", true)
      .then((response) => {
        if (response && response.data) {
          if (response.data.status) {
            setSgRegionAvailable(true);
          } else {
            setSgRegionAvailable(false);
          }
        } else {
          setSgRegionAvailable(false);
        }
      })
      .catch((error) => {
        setSgRegionAvailable(false);
        console.log(error);
      });
  };

  useEffect(() => {
    // update all partner events on top of existing partner events
    if (partnerEvents && partnerEvents.length) {
      const updatedEvents = [...allPartnerEvents];
      partnerEvents.forEach((event) => {
        // don't push event if event.key already exists in allPartnerEvents array of objects
        const existingEvent = allPartnerEvents.find((e) => e.key === event.key);
        if (!existingEvent) {
          updatedEvents.push(event);
        }
      });
      setAllPartnerEvents(updatedEvents);
    }
  }, [partnerEvents]);

  const getPartnerEvents = () => {
    PartnerDashboardService.getPartnerEventsList(true)
      .then((response) => {
        if (response && response.data) {
          setPartnerEvents(response.data);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const prepareFilters = (filters) => {
    const industries = [];
    const usecases = [];
    const technologies = [];
    if (filters.industry) {
      filters.industry.forEach((industry) => {
        industries.push({
          id: industry.value.trim().toLowerCase(),
          label: industry.key.trim(),
          value: industry.value.trim()
        });
      });
      industries.push({
        id: "others",
        label: "Others",
        value: "others"
      });
      setIndustryFilter(industries);
    }
    if (filters.category) {
      filters.category.forEach((category) => {
        usecases.push({
          id: category.value.trim().toLowerCase(),
          label: category.key.trim(),
          value: category.value.trim(),
          subCategories: category.subCategories
        });
      });
      setUseCaseCategories(usecases);
    }
    if (filters.technology) {
      filters.technology.forEach((technology) => {
        technologies.push({
          id: technology.value.trim().toLowerCase(),
          label: technology.key.trim(),
          value: technology.value.trim()
        });
      });
      setTechnologyFilter(technologies);
    }
  };

  const getPartnerCategories = () => {
    PartnerDashboardService.getPartnerCategories(true)
      .then((response) => {
        if (response && response.data) {
          prepareFilters(response.data);
          // setPartnerCategories(response.data);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const validateApp = (faction, conf) => {
    let isValid = true;
    if (faction === "edit") {
      if (conf.draftConfig.appId === null || conf.draftConfig.appId === undefined) {
        setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.app-id-unavailable-msg"), 4000, "negative");
        isValid = false;
      }
      if (conf.draftConfig.partnerTenantId === null || conf.draftConfig.partnerTenantId === undefined) {
        setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.partner-tenant-id-unavailable-msg"), 4000, "negative");
        isValid = false;
      }
    }
    // validate app center
    const appCenterKeys = ["appName", "appDescription", "appTagLine", "appIconS3Url", "appIcon", "primaryVertical"];
    appCenterKeys.forEach((key) => {
      if (conf.draftConfig[key] === null || conf.draftConfig[key] === undefined || conf.draftConfig[key].trim() === "") {
        setSelectedTab("app-center");
        setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-all-mandatory-fields-filled-in-app-center-msg"), 4000, "negative");
        isValid = false;
      }
    });

    const devInfo = conf.draftConfig.developerInfo;
    if (devInfo.companyName === null || devInfo.companyName === undefined || devInfo.companyWebsite === null || devInfo.companyWebsite === undefined) {
      setSelectedTab("app-center");
      setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-all-mandatory-fields-filled-in-app-center-msg"), 4000, "negative");
      isValid = false;
    }

    if (!isValid) {
      return false;
    }

    // validate use cases
    if (conf.draftConfig.useCases && conf.draftConfig.useCases.length) {
      conf.draftConfig.useCases.forEach((useCase) => {
        const useCaseKeys = ["headLine", "description", "category", "appIconS3Url"];
        useCaseKeys.forEach((key) => {
          if (useCase[key] === null || useCase[key] === undefined || useCase[key].trim() === "") {
            setSelectedTab("use-case");
            setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-all-mandatory-fields-filled-in-usecase-msg"), 4000, "negative");
            isValid = false;
          }
        });
      });
    }

    if (!isValid) {
      return false;
    }

    // validate app tile
    const appTileKeys = ["label", "tagline"];
    appTileKeys.forEach((key) => {
      if (conf.draftConfig.appTile[key] === null || conf.draftConfig.appTile[key] === undefined || conf.draftConfig.appTile[key].trim() === "") {
        setSelectedTab("app-tile");
        setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-all-mandatory-fields-filled-in-app-tile-msg"), 4000, "negative");
        isValid = false;
      }
    });

    const vAppType = conf.draftConfig.applicationType || type || "";

    if (vAppType == "On-Prem" || vAppType == "STC") {
      if (conf.draftConfig.appTile.activationTokenDuration === null || conf.draftConfig.appTile.activationTokenDuration === undefined || conf.draftConfig.appTile.activationTokenDuration == "") {
        setSelectedTab("app-tile");
        setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-all-mandatory-fields-filled-in-app-tile-msg"), 4000, "negative");
        isValid = false;
      }

      if (!isValid) {
        return false;
      }
      const activationTokenDuration = conf.draftConfig.appTile && conf.draftConfig.appTile.activationTokenDuration ? JSON.parse(conf.draftConfig.appTile.activationTokenDuration) : 0;
      if (!Number.isInteger(activationTokenDuration) || activationTokenDuration <= 0) {
        setSelectedTab("app-tile");
        setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.enter-valid-duration-in-mins-msg"), 4000, "negative");
        isValid = false;
      }
    }

    if (!isValid) {
      return false;
    }

    const oauthTileKeys = ["clientId", "clientSecret", "dashboardUrl", "oAuthLoginURL", "oAuthTokenURL", "partnerAppInfoURL"];
    if (conf.draftConfig.appTile && conf.draftConfig.appTile.oAuth && !conf.draftConfig.appTile.autoActivation) {
      oauthTileKeys.forEach((key) => {
        if (conf.draftConfig.appTile[key] === null || conf.draftConfig.appTile[key] === undefined || conf.draftConfig.appTile[key].trim() === "") {
          setSelectedTab("app-tile");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-all-oauth-fields-filled-msg"), 4000, "negative");
          isValid = false;
        }
      });
    }

    if (!isValid) {
      return false;
    }

    if (conf.draftConfig.appTile && conf.draftConfig.appTile.oAuth && !conf.draftConfig.appTile.autoActivation) {
      const oAuthUrl0 = conf.draftConfig.appTile.dashboardUrl;
      if (oAuthUrl0) {
        if (oAuthUrl0.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("app-tile");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-app-dashboard-url-starts-with-msg"), 4000, "negative");
          return false;
        }
      }
      const oAuthUrl1 = conf.draftConfig.appTile.oAuthLoginURL;
      if (oAuthUrl1) {
        if (oAuthUrl1.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("app-tile");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-oauth-base-url-starts-with-msg"), 4000, "negative");
          return false;
        }
      }
      const oAuthUrl2 = conf.draftConfig.appTile.oAuthTokenURL;
      if (oAuthUrl2) {
        if (oAuthUrl2.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("app-tile");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-oauth-base-url-starts-with-msg"), 4000, "negative");
          return false;
        }
      }
      const oAuthUrl3 = conf.draftConfig.appTile.partnerAppInfoURL;
      if (oAuthUrl3) {
        if (oAuthUrl3.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("app-tile");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-oauth-base-url-starts-with-msg"), 4000, "negative");
          return false;
        }
      }
    }

    if (conf.draftConfig.appTile && conf.draftConfig.appTile.dashboardUrl && !conf.draftConfig.appTile.autoActivation) {
      const appDashboardUrl = conf.draftConfig.appTile.dashboardUrl;
      if (appDashboardUrl) {
        if (appDashboardUrl.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("app-tile");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-app-dashboard-url-starts-with-msg"), 4000, "negative");
          return false;
        }
      }
    }

    if (conf.draftConfig.appTile && conf.draftConfig.appTile.healthCheckUrl) {
      const healthUrl1 = conf.draftConfig.appTile.healthCheckUrl;
      if (healthUrl1) {
        if (healthUrl1.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("behaviors");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-app-health-check-url-starts-with-ms"), 4000, "negative");
          return false;
        }
      }
    }

    if (conf.draftConfig.appTile && conf.draftConfig.appTile.healthStatusUrl) {
      const healthUrl2 = conf.draftConfig.appTile.healthStatusUrl;
      if (healthUrl2) {
        if (healthUrl2.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("behaviors");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-app-health-status-url-starts-with-msg"), 4000, "negative");
          return false;
        }
      }
    }

    if (conf.draftConfig.appTile && conf.draftConfig.appTile.appStatusUrl) {
      const healthUrl3 = conf.draftConfig.appTile.appStatusUrl;
      if (healthUrl3) {
        if (healthUrl3.toLowerCase().indexOf("https://") === 0) {
          // complies HTTPS format
        } else {
          setSelectedTab("behaviors");
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-app-status-url-starts-with-msg"), 4000, "negative");
          return false;
        }
      }
    }

    // validate events
    // if (!this.validateSavePartnerApps()) {
    //   setSelectedTab("integration");
    //   setToastMessage(
    //     i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.verify-form-in-integration-details-msg"), 4000, "negative");
    //   isValid = false;
    // } else {
    //   if (conf["draftConfig"]["endpoints"] === null || conf["draftConfig"]["endpoints"] === undefined || conf["draftConfig"]["endpoints"].length === 0) {
    //     setSelectedTab("integration");
    //     if (vAppType=="On-Prem") {
    //       setToastMessage(
    //         i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-atleast-one-pull-channel-msg"), 4000, "negative");
    //     } else {
    //       setToastMessage(
    //         i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.ensure-atleast-one-push-channel-msg"), 4000, "negative");
    //     }
    //     isValid = false;
    //   }
    // }

    // if (!isValid) {
    //   return false;
    // }
    return isValid;
  };

  const proceedToSave = (fappConfig, faction) => {
    // PartnerDashboardService.savePartnerStrapiApp(fappConfig)
    PartnerDashboardService.savePartnerApp(fappConfig)
      .then((response) => {
        if (response && response.status) {
          let msg = "";
          if (faction === "create" || faction === "copy-create") {
            msg = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.partner-app-created-success-msg");
          } else {
            msg = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.partner-app-update-success-msg");
          }
          setToastMessage(msg, 5000, "positive");
          closeManageModal();
        } else {
          setToastMessage(response.message ? response.message : i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.error-on-updating-partner-app-msg"), 4000, "negative");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const preparePartnerEvents = (dc) => {
    console.log("preparePartnerEvents:: ", dc);
    const draftConf = { ...dc };
    const reqPermissions = [];
    if (draftConf.subscriptions && draftConf.subscriptions[0]) {
      if (draftConf.subscriptions[0].eventTypes && draftConf.subscriptions[0].eventTypes.length) {
        if (PartnerEventsList.events.length) {
          PartnerEventsList.events.forEach((evt) => {
            if (draftConf.subscriptions[0].eventTypes.indexOf(evt.key) > -1) {
              if (evt.permissions) {
                evt.permissions.forEach((perm) => {
                  if (!reqPermissions.includes(perm)) {
                    reqPermissions.push(perm);
                  }
                });
              }
            }
          });
        }
      }
      if (draftConf.subscriptions[0].enableClientMAC && JSON.parse(draftConf.subscriptions[0].enableClientMAC)) {
        reqPermissions.push("MacAddressPermission");
      }
      if (draftConf.subscriptions[0].enableSocial && JSON.parse(draftConf.subscriptions[0].enableSocial)) {
        reqPermissions.push("SocialPermission");
      }
      // Create a list of unique permissions by removing duplicates from reqPermissions
      const uniquePermissions = [...new Set(reqPermissions)];
      if (uniquePermissions.length) {
        draftConf.subscriptions[0].requiredPermissions = uniquePermissions;
      }
    }
    return draftConf;
  };

  const prepareFinalEndpoints = (eApp) => {
    const eAppPushChannelKeys = ["KINESIS_FIREHOSE", "KINESIS", "EVENT_HUBS", "PUB_SUB"];
    const appCpy = { ...eApp };
    appCpy.endpoints.forEach((endpoint, i) => {
      if (eAppPushChannelKeys.includes(endpoint.type)) {
        appEndpoints.forEach((appEndpoint) => {
          if (appEndpoint.type === endpoint.type) {
            appCpy.endpoints[i].config = appEndpoint.config;
          }
        });
      }
    });
    return appCpy.endpoints;
  };

  const saveApp = () => {
    const vApp = {};
    vApp.draftConfig = { ...app };
    if (validateApp(action === "edit" ? "edit" : "create", vApp)) {
      if (action === "create" || action === "copy-create") {
        const createApp = {};
        createApp.draftConfig = { ...app };
        createApp.draftConfig = preparePartnerEvents(createApp.draftConfig);
        createApp.endpoints = prepareFinalEndpoints(createApp.draftConfig);
        createApp.applicationType = createApp.draftConfig.applicationType || type || "";
        createApp.disableSimulation = createApp.disableSimulation !== null && createApp.disableSimulation !== undefined ? JSON.parse(createApp.disableSimulation) : true;
        createApp.draftConfig.disableSimulation =
          createApp.draftConfig.disableSimulation !== null && createApp.draftConfig.disableSimulation !== undefined ? JSON.parse(createApp.draftConfig.disableSimulation) : true;
        if (sessionStorage.getItem("isInternal")) {
          createApp.isInternal = JSON.parse(sessionStorage.getItem("isInternal"));
        } else {
          createApp.isInternal = false;
        }
        createApp.isSandbox = true;
        createApp.simulationVerticals = createApp.draftConfig.simulationVerticals ? createApp.draftConfig.simulationVerticals : [];
        createApp.applicationType = type;
        delete createApp.draftConfig.appId;
        if (createApp.editType) delete createApp.editType;

        proceedToSave(createApp, "create");
      } else if (action === "edit") {
        const editApp = {};
        editApp.draftConfig = { ...app };
        editApp.draftConfig = preparePartnerEvents(editApp.draftConfig);
        editApp.endpoints = prepareFinalEndpoints(editApp.draftConfig);
        if (editApp.draftConfig.applicationType) {
          editApp.applicationType = editApp.draftConfig.applicationType;
        } else if (type) {
          editApp.draftConfig.applicationType = type;
          editApp.applicationType = type;
        }
        editApp.disableSimulation = editApp.draftConfig.disableSimulation !== null && editApp.draftConfig.disableSimulation !== undefined ? JSON.parse(editApp.draftConfig.disableSimulation) : true;
        editApp.draftConfig.disableSimulation = editApp.disableSimulation;
        editApp.isInternal = editApp.draftConfig.isInternal;
        editApp.isSandbox = editApp.draftConfig.isSandbox;
        editApp.simulationVerticals = editApp.draftConfig.simulationVerticals ? editApp.draftConfig.simulationVerticals : [];
        editApp.appId = editApp.draftConfig.appId;
        if (editApp.editType) delete editApp.editType;

        proceedToSave(editApp, "edit");
      }
    } else {
      // invalid app config
    }
    console.log("Save App", app);
  };

  const setAppDynamic = (e) => {
    console.log(e);
    setApp(e);
  };

  const getButtonLabel = (faction) => {
    switch (faction) {
      case "create":
        return i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.create-text");
      case "copy-create":
        return i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.create-text");
      case "edit":
        return i18n.t("partner-dashboard-app.partner-apps.partner-app-center.save-text");
      default:
        return ""; // or a default text if needed
    }
  };

  const getAppTypeIcon = (ftype) => {
    return ftype === "On-Prem" ? OnpremIcon : CloudIcon;
  };
  const getActionText = (faction) => {
    switch (faction) {
      case "create":
        return i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.create-new-app-text");
      case "copy-create":
        return i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.create-new-app-text");
      case "edit":
        return i18n.t("partner-dashboard-app.titles.edit-app");
      case "view":
        return i18n.t("partner-dashboard-app.titles.view-app");
      default:
        return "";
    }
  };
  const appTypeIcon = getAppTypeIcon(type);
  const actionText = getActionText(action);
  const appName = app ? app.appName : "";

  const keyRotationRenew = (keyObj) => {
    setLoading(true);
    PartnerDashboardService.getKeyRotationRenew(keyObj)
      .then((response) => {
        if (response && response.status) {
          setToastMessage(`${keyObj.setup} ${i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.key-update-success-msg")}`, 4000, "positive");
          setLoading(false);
          const drApp = { ...app };
          drApp.apiKeys = response.data.apiKey;
          setApp(drApp);
        } else {
          setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.error-on-renew-key-msg"), 4000, "negative");
          setLoading(false);
        }
      })
      .catch((error) => {
        setToastMessage(i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.error-on-renew-key-msg"), 4000, "negative");
        console.log(error);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (props.app.editType === "live") {
      setSelectedTab("integration-details");
    }
    console.log("ManageApp Mount", action, props.app, DefaultConfig);
    // Get Region Status for Partner
    getPartnerRegionStatus();
    console.log(`${partnerEvents} ${appConfig}`);

    // Get App Config
    if (action === "edit" || action === "view") {
      if (props.app.editType === "live") {
        setApp(props.app);
      } else {
        const drApp = { ...props.app.draftConfig };
        drApp.editType = "draft";
        setApp(drApp);
        setLiveConfig(props.app);
      }
    } else if (action === "copy-create") {
      const createApp = { ...props.app };
      delete createApp._id;
      delete createApp.stage;
      delete createApp.version;
      delete createApp.draftConfig.stage;
      delete createApp.draftConfig.version;
      if (createApp.appId) {
        createApp.appId = DefaultConfig.appId;
        createApp.draftConfig.appId = DefaultConfig.draftConfig.appId;
        createApp.appTile.clientId = DefaultConfig.draftConfig.appTile.clientId;
        createApp.appTile.clientSecret = DefaultConfig.draftConfig.appTile.clientSecret;
      }

      setApp(createApp.draftConfig);
      setLiveConfig(createApp);
    } else {
      let createAppUUID = uuidv4();
      createAppUUID = createAppUUID.replace(/-/g, "").toLowerCase();
      let appendStr = "";
      appendStr = uuidv4().replace(/-/g, "").toLowerCase();
      createAppUUID += appendStr.substring(0, 8);

      let clientSecretUUID = uuidv4();
      clientSecretUUID = clientSecretUUID.replace(/-/g, "").toLowerCase();
      let appendStr1 = "";
      appendStr1 = uuidv4().replace(/-/g, "").toLowerCase();
      clientSecretUUID += appendStr1.substring(0, 8);

      // create deep copy of DefaultConfig
      const defaultConfigCpy = JSON.parse(JSON.stringify(DefaultConfig));

      defaultConfigCpy.appId = createAppUUID;
      defaultConfigCpy.draftConfig.appTile = {
        enableAutoActivation: false,
        autoActivation: false,
        autoActivationURL: null,
        metricApi: "",
        statsDashboardUrl: "",
        redirectUri: "",
        clientId: "",
        healthCheckUrl: "",
        metric: "",
        partnerDashboardUrl: "",
        scope: "",
        tagline: "",
        clientSecret: "",
        label: "",
        oAuth: false,
        instructionNotes: `${i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.to-activate-app-msg")} ${i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.paste-copied-activation-token-msg")}`,
        launchInstructions: {
          isNotes: false,
          link: "",
          notes: `${i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.launch-your-app-msg")}`
        }
      };
      if (action === "create" && type !== "MTC") {
        defaultConfigCpy.draftConfig.appTile.activationTokenDuration = 60;
      }
      defaultConfigCpy.draftConfig.appId = createAppUUID;
      defaultConfigCpy.draftConfig.appTile.clientId = "dnaspaces";
      defaultConfigCpy.draftConfig.appTile.clientSecret = clientSecretUUID;
      const baseUrl = properties.baseUrl ? properties.baseUrl : "";
      defaultConfigCpy.draftConfig.appTile.redirectUri = `${baseUrl}/partner/OAuthValidation`;
      setApp(defaultConfigCpy.draftConfig);
    }
    // APIs
    getPartnerEvents();
    getPartnerCategories();

    return () => {
      console.log("ManageApp Destroy");
    };
  }, []);

  const confirmBackTemplate = () => {
    return (
      <Modal className="browser-back-confirm-modal" isOpen={confirmBrowserBackModal}>
        <Flex direction="vertical">
          <Heading
            as="h2"
            margin={{
              bottom: 16
            }}
            size="primary"
          >
            Are you sure you want to go back to home page?
          </Heading>
          <MNotification style={{ marginBottom: 24 }} status="warning">
            Any unsaved changes will be lost.
          </MNotification>
          <Flex justify="flex-end">
            <Button.Group>
              <Button
                kind="secondary"
                onClick={() => {
                  leaveBrowserModalPage();
                }}
              >
                Discard Changes and leave this page
              </Button>
              <Button
                id="close-browser-back-modal"
                onClick={() => {
                  closeBrowserBackModal();
                }}
              >
                Stay here
              </Button>
            </Button.Group>
          </Flex>
        </Flex>
      </Modal>
    );
  };

  const getAppStageText = () => {
    let appStageText = "";
    switch (props.app.stage) {
      case "NEW":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.draft-caps-text");
        break;
      case "DRAFT":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.draft-caps-text");
        break;
      case "LIVE":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.live-caps-text");
        break;
      case "TEST":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.testing-caps-text");
        break;
      case "SUBMIT":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.submitted-caps-text");
        break;
      case "REJECTED":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.not-approved-caps-text");
        break;
      case "UNDER-REVIEW":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.under-review-caps-text");
        break;
      case "UNDER-REVIEW-COMING-SOON":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.under-review-caps-text");
        break;
      case "APPROVED":
        appStageText = i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.approved-caps-text");
        break;
      default:
        appStageText = "";
        break;
    }
    return `| ${appStageText}`;
  };

  const manageAppTemplate = () => {
    return (
      <Modal className="manage-app-modal" size="xl" isOpen={isManageAppModalOpen}>
        <Flex id="choose-app-type-modal" style={{ "padding-bottom": "10px", "border-bottom": "1px solid #DDDFE2" }} justify="space-between">
          {confirmBrowserBackModal ? confirmBackTemplate() : ""}
          <Heading style={{ "margin-bottom": "10px" }} id="create-app-heading">
            {`${actionText}: ${appName || ""}`}
            <img className="appType" src={appTypeIcon} alt="" />
            {action !== "create" ? ` | ` : ""}
            {action !== "create" && props.app.region === "us" && `${i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.rest-of-the-world-text")}`}
            {action !== "create" && props.app.region === "eu" && `${i18n.t("partner-dashboard-app.partner-apps.partner-app-center.europe-region-text")}`}
            {action !== "create" && props.app.region === "sg" && `${i18n.t("partner-dashboard-app.partner-apps.partner-app-center.singapore-region-text")}`}
            {action !== "create" && getAppStageText()}
            {action !== "create" && props.app.editType === "live" && ` | ${props.app.version}`}
            {action !== "create" && props.app.editType === "draft" && props.app.draftConfig && props.app.draftConfig.version ? ` | ${props.app.draftConfig.version}` : ""}
          </Heading>
          <Flex justify="flex-end">
            <Button.Group>
              <Button
                kind="tertiary"
                onClick={() => {
                  closeManageModal();
                }}
              >
                {i18n.t("partner-dashboard-app.auth.change-password.cancel-text")}
              </Button>
              {action !== "view" && (
                <Button
                  id="create-save-app-btn"
                  onClick={() => {
                    saveApp();
                  }}
                >
                  {getButtonLabel(action)}
                </Button>
              )}
            </Button.Group>
          </Flex>
        </Flex>
        <Modal.Content
          style={{
            maxHeight: "100%",
            minHeight: "100%",
            borderTop: 0
          }}
        >
          <Container style={{ border: "none", "box-shadow": "none", padding: "24px 0" }}>
            <div>
              <MTabs.Vertical className="stickyTabs" kind="secondary">
                {action === "create" || action === "copy-create" || action === "view" || props.app.editType === "draft" ? (
                  <MTabs.Link id="app-center-tab" onClick={() => setSelectedTab("app-center")} selected={selectedTab == "app-center"}>
                    {i18n.t("partner-dashboard-app.partner-apps.app-center.app-center-text")} *
                  </MTabs.Link>
                ) : (
                  ""
                )}
                {action === "create" || action === "copy-create" || action === "view" || props.app.editType === "draft" ? (
                  <MTabs.Link id="use-case-tab" onClick={() => setSelectedTab("use-case")} selected={selectedTab == "use-case"}>
                    {i18n.t("partner-dashboard-app.partner-apps.partner-apps-list.usecases-text")}
                  </MTabs.Link>
                ) : (
                  ""
                )}
                {action === "create" || action === "copy-create" || action === "view" || props.app.editType === "draft" ? (
                  <MTabs.Link id="app-tile-tab" onClick={() => setSelectedTab("app-tile")} selected={selectedTab == "app-tile"}>
                    {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.app-tile-text")} *
                  </MTabs.Link>
                ) : (
                  ""
                )}
                {action === "create" || action === "copy-create" || action === "view" || props.app.editType === "draft" ? (
                  <MTabs.Link id="behaviors-tab" onClick={() => setSelectedTab("behaviors")} selected={selectedTab == "behaviors"}>
                    {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.behaviors-text")}
                  </MTabs.Link>
                ) : (
                  ""
                )}
                {action === "create" || action === "copy-create" || action === "view" || props.app.editType === "draft" ? (
                  <MTabs.Link id="events-tab" onClick={() => setSelectedTab("events")} selected={selectedTab == "events"}>
                    {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.events-text")}
                  </MTabs.Link>
                ) : (
                  ""
                )}
                <MTabs.Link id="integration-details-tab" onClick={() => setSelectedTab("integration-details")} selected={selectedTab == "integration-details"}>
                  {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.integration-details-text")}
                </MTabs.Link>
                {props.app.editType === "draft" &&
                  action !== "create" &&
                  props.app.subscriptions[0] &&
                  props.app.subscriptions[0].customTerms &&
                  props.app.subscriptions[0].customTerms.description && (
                    <MTabs.Link id="terms-and-conditions-tab" onClick={() => setSelectedTab("terms-and-conditions")} selected={selectedTab == "terms-and-conditions"}>
                      {i18n.t("partner-dashboard-app.partner-apps.partner-apps-view.terms-and-conditions-text")}
                    </MTabs.Link>
                  )}
              </MTabs.Vertical>
              <div className="tabCont">
                {selectedTab == "app-center" && (
                  <div className="tab-content">
                    <AppCenterTab euRegionAvailable={euRegionAvailable} sgRegionAvailable={sgRegionAvailable} app={app} industryFilter={industryFilter} setApp={setAppDynamic} />
                  </div>
                )}
                {selectedTab == "use-case" && (
                  <div className="tab-content">
                    <UseCaseTab app={app} action={action} useCaseCategories={useCaseCategories} technologyFilter={technologyFilter} setApp={setAppDynamic} />
                  </div>
                )}
                {selectedTab == "app-tile" && (
                  <div className="tab-content">
                    <AppTileTab action={action} type={type} app={app} setApp={setAppDynamic} />
                  </div>
                )}
                {selectedTab == "behaviors" && (
                  <div className="tab-content">
                    <BehavioursTab app={app} setApp={setAppDynamic} />
                  </div>
                )}
                {selectedTab == "events" && (
                  <div className="tab-content">
                    <EventsTab app={app} setApp={setAppDynamic} events={allPartnerEvents} />
                  </div>
                )}
                {selectedTab == "integration-details" && (
                  <div className="tab-content">
                    <IntegrationDetailsTab
                      action={action}
                      appEndpoints={appEndpoints}
                      setAppEndpoints={setAppEndpoints}
                      type={type}
                      awsRegions={AWSRegionJson.regions}
                      app={app}
                      setApp={setAppDynamic}
                      keyRotationRenew={keyRotationRenew}
                    />
                  </div>
                )}
                {selectedTab == "terms-and-conditions" && (
                  <div className="tab-content">
                    <CustomTncTab app={app} setApp={setAppDynamic} />
                  </div>
                )}
              </div>
            </div>
          </Container>

          {/* <Flex direction="horizontal" style={{ margin: "10px" }}>
            <Flex direction="horizontal" justify="flex-start">
              <Flex direction="horizontal">
                <Flex direction="vertical">
                  
                </Flex>
                <Flex direction="vertical">
                  <Flex direction="vertical">
                    <Flex direction="column">
                      
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex> */}
        </Modal.Content>
      </Modal>
    );
  };

  return app && (app.appId !== null || app.appId !== undefined) && manageAppTemplate();
};

export default ManageApp;
